import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, KaeltenachweisAnnahme, KaeltenachweisAntwort, KaeltenachweisBefund, KaeltenachweisErgebnis,
  KaeltenachweisNormverweis, KaeltenachweisProfilzeile,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, annahmenVorbehalt, formatiereZahl, type Spalte,
} from '../muster';
import {
  BEREICHE, BEREICH_BESCHREIBUNG, BEREICH_TITEL, KLASSEN, matrix, matrixText, nachkomma,
  rechenstandNachweis, regel, zustandErgebnis, zustandHerkunftNachweis, type MatrixZeile,
} from './heizung/kaeltenachweis';

/**
 * Der Kältenachweis – Kälte-Kern Stufe 1 und DIN EN 378 (TH-3, KG 434).
 *
 * ═══ Was die Seite zeigt ══════════════════════════════════════════════════
 *
 * Den jüngsten beendeten Lauf von `kaelte-rechnen`: Erzeugung je Maschine
 * und als Anlage, Teillast und Jahresarbeit, n−1 der Erzeugung und der
 * Rückkühlung, das Eisspeicherprofil am Auslegungstag, die freie Kühlung
 * und den Nachweis nach DIN EN 378 (R1234ze, A2L). Die Seite rechnet
 * nichts; Bericht KAE-KMN und Excel-Liste lesen dasselbe Datenmodell.
 *
 * ═══ Warum fast alles „vorläufig“ oder „nicht prüfbar“ heißt ══════════════
 *
 * Ein Ergebnis heißt nur „erfüllt“, wenn alle genutzten Eingaben bestätigt
 * sind. Füllmenge, Aufstellraum, Gaswarnung und Notlüftung sind offen
 * (HZK-31), die EN-378-Grenzwerte ungeprüft – bis zur Antwort steht dort
 * „vorläufig“ oder „nicht prüfbar“, nie „erfüllt“.
 *
 * TX-STANDARD S1: PageHeader mit Rechenstand, Vorbehalte, höchstens sechs
 * Kennzahlen, DataTable, Fachstatus S1.5, Annahmenliste S1.6, Normbezug.
 * Exportleiste (TH-2b): PDF KAE-KMN über die Berichtserzeugung (gleicher Stand
 * liefert die abgelegte Fassung, F11) und die Excel-Mappe „Kältenachweis“.
 */

interface Rahmen { api: ApiClient; projekt: { id: string } }

/** Mehrzeiliger Text ohne Inline-Stil. */
function Zeilen({ text }: { text: string | null | undefined }) {
  if (!text) return <>—</>;
  const teile = text.split('\n');
  return (
    <>
      {teile.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>{t}{i < teile.length - 1 && <br />}</span>
      ))}
    </>
  );
}

function Ergebnis({ e }: { e: KaeltenachweisErgebnis }) {
  const s = zustandErgebnis(e.ergebnis);
  return <StatusBadge klein zustand={s.zustand} text={s.text} />;
}

const ERGEBNISSE: Spalte<KaeltenachweisErgebnis>[] = [
  { schluessel: 'pruefung', titel: 'Prüfung', immerSichtbar: true, wert: (e) => e.bezeichnung },
  { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (e) => e.gegenstand, versteckt: true },
  { schluessel: 'wert', titel: 'Wert', art: 'zahl', wert: (e) => e.wert,
    inhalt: (e) => formatiereZahl(e.wert, nachkomma(e.einheit)) },
  { schluessel: 'vergleich', titel: 'Vergleich', wert: (e) => e.vergleich ?? '' },
  { schluessel: 'grenze', titel: 'Grenze', art: 'zahl', wert: (e) => e.grenzwert,
    inhalt: (e) => formatiereZahl(e.grenzwert, nachkomma(e.einheit)) },
  { schluessel: 'einheit', titel: 'Einheit', wert: (e) => e.einheit ?? '' },
  { schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status',
    wert: (e) => zustandErgebnis(e.ergebnis).text, inhalt: (e) => <Ergebnis e={e} /> },
  { schluessel: 'fachfrage', titel: 'Fachfrage', wert: (e) => e.fachfrage ?? '' },
  { schluessel: 'norm', titel: 'Normbezug', wert: (e) => e.normbezug ?? '', versteckt: true },
];

const PROFIL: Spalte<KaeltenachweisProfilzeile>[] = [
  { schluessel: 'stunde', titel: 'Stunde', art: 'zahl', nachkomma: 0, immerSichtbar: true, wert: (p) => p.stunde },
  { schluessel: 'aussen', titel: 'Außentemperatur', einheit: '°C', art: 'zahl', nachkomma: 1, wert: (p) => p.aussenC },
  { schluessel: 'last', titel: 'Last', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (p) => p.lastKw },
  { schluessel: 'maschinen', titel: 'Maschinen', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (p) => p.maschinenKw },
  { schluessel: 'entzug', titel: 'Entzug Speicher', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (p) => p.entzugKw },
  { schluessel: 'ladung', titel: 'Ladung Speicher', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (p) => p.ladungKw },
  { schluessel: 'ungedeckt', titel: 'ungedeckt', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (p) => p.ungedecktKw },
  { schluessel: 'ladezustand', titel: 'Ladezustand', einheit: 'kWh', art: 'zahl', nachkomma: 1, wert: (p) => p.ladezustandKwh },
];

const ANNAHMEN: Spalte<KaeltenachweisAnnahme>[] = [
  { schluessel: 'parameter', titel: 'Parameter', immerSichtbar: true, wert: (a) => a.parameter },
  { schluessel: 'wert', titel: 'Wert', wert: (a) => a.wert ?? 'offen' },
  { schluessel: 'einheit', titel: 'Einheit', wert: (a) => a.einheit ?? '' },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (a) => zustandHerkunftNachweis(a.herkunft).text,
    inhalt: (a) => { const s = zustandHerkunftNachweis(a.herkunft); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
  { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (a) => [a.quelle, a.normbezug].filter(Boolean).join(' · ') },
  { schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status',
    wert: (a) => (a.bestaetigt ? 'bestätigt' : 'unbestätigt'),
    inhalt: (a) => (
      <StatusBadge klein zustand={a.bestaetigt ? 'gut' : 'warnung'} text={a.bestaetigt ? 'bestätigt' : 'unbestätigt'} />
    ) },
];

const BEFUNDE: Spalte<KaeltenachweisBefund>[] = [
  { schluessel: 'schwere', titel: 'Schwere', art: 'status',
    wert: (b) => (b.schwere === 'F' ? 'Fehler' : b.schwere === 'W' ? 'Warnung' : 'Hinweis'),
    inhalt: (b) => (
      <StatusBadge klein zustand={b.schwere === 'F' ? 'fehler' : b.schwere === 'W' ? 'warnung' : 'info'}
        text={b.schwere === 'F' ? 'Fehler' : b.schwere === 'W' ? 'Warnung' : 'Hinweis'} />
    ) },
  { schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel },
  { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (b) => b.gegenstand },
  { schluessel: 'meldung', titel: 'Meldung', wert: (b) => b.meldung },
];

const NORMEN: Spalte<KaeltenachweisNormverweis>[] = [
  { schluessel: 'norm', titel: 'Norm', immerSichtbar: true, wert: (n) => n.norm },
  { schluessel: 'ausgabe', titel: 'Ausgabe', wert: (n) => n.ausgabe ?? '—' },
  { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (n) => n.abschnitt ?? '—' },
  { schluessel: 'anwendung', titel: 'Anwendung', wert: (n) => n.anwendung },
];

const MATRIX: Spalte<MatrixZeile>[] = [
  { schluessel: 'kategorie', titel: 'Zugangskategorie', immerSichtbar: true, wert: (m) => m.kategorie },
  ...KLASSEN.map((k): Spalte<MatrixZeile> => ({
    schluessel: `klasse_${k}`, titel: `Klasse ${k}`,
    wert: (m) => matrixText(m.zellen[k], formatiereZahl),
    inhalt: (m) => {
      const z = m.zellen[k];
      const text = matrixText(z, formatiereZahl);
      return z && z.bezeichnung.includes('(angenommen)')
        ? <><strong>{text}</strong> <StatusBadge klein zustand="info" text="angenommen" /></>
        : text;
    },
  })),
];

/** Eine Kennzahl aus einer Prüfzeile: Wert in Prozent der Grenze, Zustand nur, wo bewertet wird (S1.2). */
function Deckung({ titel, e }: { titel: string; e: KaeltenachweisErgebnis | undefined }) {
  if (!e || e.wert === null || e.grenzwert === null || e.grenzwert === 0) {
    return <KpiCard titel={titel} wert={null} vergleich={e ? zustandErgebnis(e.ergebnis).text : 'nicht gerechnet'} />;
  }
  const s = zustandErgebnis(e.ergebnis);
  const bewertet = e.ergebnis !== 'hinweis' && e.ergebnis !== 'entfaellt';
  return (
    <KpiCard titel={titel} wert={(e.wert / e.grenzwert) * 100} einheit="%" nachkomma={1}
             zustand={bewertet ? s.zustand : undefined} zustandText={bewertet ? s.text : undefined}
             vergleich={`${formatiereZahl(e.wert, 1)} von ${formatiereZahl(e.grenzwert, 1)} ${e.einheit ?? ''}`.trim()} />
  );
}

function Bereich({ bereich, zeilen }: { bereich: string; zeilen: KaeltenachweisErgebnis[] }) {
  if (bereich === 'en378_matrix') {
    return (
      <ContentSection titel={BEREICH_TITEL[bereich]} beschreibung={BEREICH_BESCHREIBUNG[bereich]}>
        <DataTable name="Füllmengengrenzen DIN EN 378" spalten={MATRIX} zeilen={matrix(zeilen)}
                   schluessel={(m) => m.kategorie} suche={false} dicht
                   leer={<EmptyState klein titel="Keine Matrix gerechnet." />} />
        <p className="schwach">
          Grenzen aus den ungeprüften Normwerten (Migration 1251). „nicht prüfbar“: Regel nicht hinterlegt oder
          Raumvolumen fehlt – es wird keine Grenze erfunden. a = allgemeiner, b = beaufsichtigter, c = Zugang nur
          für befugte Personen.
        </p>
      </ContentSection>
    );
  }

  return (
    <ContentSection titel={BEREICH_TITEL[bereich] ?? bereich} beschreibung={BEREICH_BESCHREIBUNG[bereich]}>
      <DataTable name={BEREICH_TITEL[bereich] ?? bereich} spalten={ERGEBNISSE} zeilen={zeilen}
                 schluessel={(e) => `${e.regel}-${e.reihenfolge}`} suche={false} dicht
                 leer={<EmptyState klein titel="Keine Prüfung in diesem Bereich." />} />
      {zeilen.map((e) => (
        <details key={`weg-${e.regel}-${e.reihenfolge}`}>
          <summary>Rechenweg: {e.bezeichnung}</summary>
          <p className="schwach"><Zeilen text={e.rechenweg} /></p>
          {e.normbezug && <p className="schwach">Normbezug: {e.normbezug}</p>}
          {e.quellen && <p className="schwach">Quellen: {e.quellen}</p>}
        </details>
      ))}
    </ContentSection>
  );
}

export function KaelteNachweis() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <KaelteNachweisAnsicht api={api} projektId={projekt.id} />;
}

export function KaelteNachweisAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<KaeltenachweisAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const antwort = await api.kaelteNachweis(projektId);
      setDaten(antwort ?? null);
      if (!antwort) setFehler('Die Antwort war leer.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const lauf = daten?.lauf ?? null;
  const modell = daten?.modell ?? null;

  const kopf = (
    <PageHeader
      titel="Kältenachweis"
      beschreibung={'Kälteerzeugung, Teillast, n−1, Rückkühlung, Eisspeicher, freie Kühlung und DIN EN 378 – '
        + 'gerechnet vom Lauf „kaelte-rechnen“, mit Annahmen und Normbezug.'}
      meta={daten ? rechenstandNachweis(lauf) : undefined}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'pdf', beschriftung: 'PDF Kältenachweis', laden: () => api.kaelteNachweisPdf(projektId),
            gesperrt: lauf && modell ? undefined : { grund: 'Noch kein Lauf – erst „kaelte-rechnen“ rechnen.' } },
          { art: 'excel', laden: () => api.kaelteNachweisExcel(projektId),
            gesperrt: lauf && modell ? undefined : { grund: 'Noch kein Lauf – ohne Bestand gibt es keine Liste.' } },
        ]} />
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Der Kältenachweis konnte nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Kältenachweis wird geladen …" /></>;

  if (!lauf || !modell) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Noch kein Kältenachweis gerechnet."
          erklaerung={'Der Nachweis entsteht mit dem Werkzeug „kaelte-rechnen <Projekt> --schreiben“ gegen den '
            + 'maßgebenden Kühllast-Lauf. Die Kältebilanz in drei Lesarten steht auf der Seite „Kälte“.'} />
        <p className="schwach"><Link to="../kaelte">Zur Seite „Kälte“</Link></p>
      </>
    );
  }

  const zeilen = [...(modell.ergebnisse ?? [])].sort((a, b) => a.reihenfolge - b.reihenfolge);
  const annahmen = modell.annahmen ?? [];
  const offen = annahmen.filter((a) => !a.bestaetigt).length;
  const profil = modell.profil ?? [];
  const en378 = zeilen.filter((z) => z.bereich === 'en378');
  const en378Offen = en378.filter((z) => z.ergebnis === 'nicht_pruefbar').length;
  const fkStunden = regel(zeilen, 'fk_stunden');

  return (
    <>
      {kopf}

      {/* Vorbehalte direkt unter dem Kopf im gemeinsamen Kasten (TX3, S1.2 Nr. 2) – seit TH-2. */}
      <Vorbehalte eintraege={[
        ...(offen > 0 ? [annahmenVorbehalt(offen, annahmen.length)] : []),
        ...(en378Offen > 0 ? [{ art: 'fachfrage' as const, verweis: 'Fachfragen Heizung/Kälte',
          text: `HZK-31, HZK-40: DIN EN 378 – ${en378Offen} Prüfung(en) nicht prüfbar; Kältemittelfüllmenge je Kreis, `
            + 'Aufstellraum, Gaswarnung und Notlüftung fehlen, die Grenzwerte sind ungeprüft – Antwort des Eigentümers steht aus.' }] : []),
        ...(modell.kuehllastVorlaeufig ? [{ art: 'sonstiges' as const,
          text: `Kühllast-Lauf ${modell.kuehllastLaufnummer ?? '—'} ist vorläufig. Alle Ergebnisse stehen unter demselben `
            + 'Vorbehalt; Ergebnisse auf unbestätigten Eingaben heißen „vorläufig“, nie „erfüllt“.' }] : []),
        ...(daten.veraltet ? [{ art: 'veraltet' as const,
          text: `Der Nachweis steht auf Kühllast-Lauf ${lauf.kuehllastLaufnummer ?? '—'}, maßgebend ist Lauf `
            + `${daten.massgebendeKuehllast?.laufnummer}. Mit „kaelte-rechnen“ neu zu rechnen.` }] : []),
      ]} />

      <KpiReihe>
        <Deckung titel={`Erzeugung (Lesart ${modell.lesart})`} e={regel(zeilen, 'erz_leistung')} />
        <Deckung titel="n−1 ohne Eisspeicher" e={regel(zeilen, 'red_erzeugung')} />
        <KpiCard titel="n−1 mit Eisspeicher (Auslegungstag)"
                 wert={regel(zeilen, 'red_erzeugung_profil')?.wert ?? null} einheit="kWh" nachkomma={1}
                 vergleich={`ungedeckt · ${zustandErgebnis(regel(zeilen, 'red_erzeugung_profil')?.ergebnis ?? '—').text}`} />
        <Deckung titel="Rückkühlung" e={regel(zeilen, 'rk_maschinensumme')} />
        <KpiCard titel="DIN EN 378 nicht prüfbar" wert={en378Offen}
                 vergleich={`von ${en378.length} Prüfungen · HZK-31`} />
        <KpiCard titel="Freikühlstunden" wert={fkStunden?.wert ?? null} einheit="h/a" nachkomma={0}
                 vergleich="obere Schätzung · HZK-33" />
      </KpiReihe>

      {BEREICHE.map((b) => {
        const teil = zeilen.filter((z) => z.bereich === b);
        return teil.length === 0 ? null : <Bereich key={b} bereich={b} zeilen={teil} />;
      })}

      <ContentSection
        titel="Eisspeicher am Auslegungstag"
        beschreibung={'Stunde für Stunde: Last des Kühllast-Laufs, Maschinenleistung, Entzug und Ladung des '
          + 'Speichers, ungedeckte Leistung und Ladezustand – eingeschwungener Tag.'}
        aufklappbar standardOffen={false}
      >
        {['n', 'n_minus_1'].map((fall) => {
          const stunden = profil.filter((p) => p.fall === fall).sort((a, b) => a.stunde - b.stunde);
          if (stunden.length === 0) return null;
          const name = fall === 'n' ? 'Alle Maschinen' : 'n−1 Maschinen';
          return (
            <DataTable key={fall} name={`Tagesprofil ${name}`} beschreibung={name} spalten={PROFIL}
                       zeilen={stunden} schluessel={(p) => `${p.fall}-${p.stunde}`} suche={false} dicht
                       ausfuhr ausfuhrDateiname={`kaelte-tagesprofil-${fall}.csv`}
                       leer={<EmptyState klein titel="Kein Tagesprofil." />} />
          );
        })}
        {profil.length === 0 && (
          <EmptyState klein titel="Kein Tagesprofil."
                      erklaerung="Der maßgebende Kühllast-Lauf hat keinen Tagesgang oder es fehlt die Maschinenleistung." />
        )}
      </ContentSection>

      <ContentSection titel="Befunde"
                      beschreibung="Was nicht erfüllt oder nicht prüfbar ist – mit Fachfrage.">
        <DataTable name="Befunde" spalten={BEFUNDE} zeilen={modell.befunde ?? []}
                   schluessel={(b) => `${b.schwere}-${b.regel}-${b.gegenstand}`} suche={false} dicht
                   ausfuhr={(modell.befunde ?? []).length > 20} ausfuhrDateiname="kaelte-befunde.csv"
                   leer={<EmptyState klein titel="Keine Befunde." erklaerung="Kein Ergebnis „nicht erfüllt“ oder „nicht prüfbar“." />} />
      </ContentSection>

      <ContentSection
        titel="Annahmen und Vorgaben"
        beschreibung={'Jede genutzte Vorgabe, Annahme und jeder Normwert – dieselbe Liste wie im Annahmenblatt '
          + 'des Berichts KAE-KMN. Unbestätigte stehen oben.'}
      >
        <DataTable name="Annahmen und Vorgaben" spalten={ANNAHMEN} zeilen={annahmen}
                   schluessel={(a) => a.parameter} suche={annahmen.length > 20} dicht
                   ausfuhr={annahmen.length > 20} ausfuhrDateiname="kaelte-nachweis-annahmen.csv"
                   leer={<EmptyState klein titel="Keine Annahmen genutzt." />} />
      </ContentSection>

      <ContentSection titel="Grundlagen und Normbezug" beschreibung={modell.datenstand}>
        <DataTable name="Normbezug" spalten={NORMEN} zeilen={modell.normbezug ?? []}
                   schluessel={(n) => n.norm} suche={false} dicht
                   leer={<EmptyState klein titel="Kein Normbezug." />} />
        {lauf.hinweise.length > 0 && (
          <ul className="liste">
            {lauf.hinweise.map((h) => <li key={h}>{h}</li>)}
          </ul>
        )}
        <p className="schwach">
          Bericht KAE-KMN und Excel-Mappe oben in der Exportleiste (gleicher Stand liefert die abgelegte Fassung).
          Die Kältebilanz in drei Lesarten steht auf der Seite{' '}
          <Link to="../kaelte">„Kälte“</Link>.
        </p>
      </ContentSection>
    </>
  );
}
